'use client';
import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { Package, Star, Mail, TrendingUp, CheckCircle, ArrowRight, Zap, Crown, Rocket } from 'lucide-react';
import CryptoPaymentModal from '@/components/CryptoPaymentModal';
import { useWalletAuth } from '@/contexts/WalletAuthContext';

interface AirdropPackage {
  id: string;
  name: string;
  package_key: string;
  price: number;
  description: string;
  features: string[];
  email_count: number;
  is_trending: boolean;
  is_recommended: boolean;
  promo_days: number;
  sort_order: number;
}

// Static fallback — always shows the correct 10 packages in ascending order
const STATIC_PACKAGES: AirdropPackage[] = [
  {
    id: 'static-free', name: 'Free Listing', package_key: 'free', price: 0, sort_order: 1,
    description: 'Get your airdrop listed on AIDARAM at no cost. Basic visibility for your project.',
    features: ['Basic airdrop listing', 'Project profile page', 'Community discovery', 'Standard search visibility'],
    email_count: 0, is_trending: false, is_recommended: false, promo_days: 0,
  },
  {
    id: 'static-boost', name: 'Boost', package_key: 'boost', price: 299, sort_order: 2,
    description: 'Boost your listing visibility and reach more airdrop hunters with enhanced placement.',
    features: ['Everything in Free Listing', 'Boosted search ranking', 'Featured badge on listing', '7-day trending placement', 'Social media mention'],
    email_count: 0, is_trending: false, is_recommended: false, promo_days: 7,
  },
  {
    id: 'static-starter_launch', name: 'Starter Launch', package_key: 'starter_launch', price: 499, sort_order: 3,
    description: 'Perfect for new projects launching their first airdrop campaign with email outreach.',
    features: ['Everything in Boost', '5,000 email blast to verified users', 'Authentic Telegram ads placement', 'Priority listing position', '14-day trending placement', 'Campaign analytics dashboard'],
    email_count: 5000, is_trending: false, is_recommended: false, promo_days: 14,
  },
  {
    id: 'static-growth_starter', name: 'Growth Starter', package_key: 'growth_starter', price: 999, sort_order: 4,
    description: 'Scale your reach with a larger email campaign and extended promotional period.',
    features: ['Everything in Starter Launch', '10,000 email blast to verified users', 'Extended Telegram promotion', '21-day trending placement', 'Dedicated campaign page', 'Weekly performance report'],
    email_count: 10000, is_trending: false, is_recommended: false, promo_days: 21,
  },
  {
    id: 'static-enterprise', name: 'Enterprise', package_key: 'enterprise', price: 2499, sort_order: 5,
    description: 'Enterprise-grade campaign for serious projects targeting a large verified audience.',
    features: ['Everything in Growth Starter', '30,000 email blast to verified users', 'Multi-channel Telegram promotion', '30-day trending placement', 'Featured homepage banner', 'Dedicated account manager', 'Real-time analytics'],
    email_count: 30000, is_trending: true, is_recommended: false, promo_days: 30,
  },
  {
    id: 'static-enterprise_pro', name: 'Enterprise Pro', package_key: 'enterprise_pro', price: 4999, sort_order: 6,
    description: 'Maximum enterprise exposure with 50,000 targeted email blasts and premium placement.',
    features: ['Everything in Enterprise', '50,000 email blast to verified users', 'Premium Telegram channel ads', '45-day trending placement', 'Homepage hero feature', 'Priority support', 'Custom campaign design'],
    email_count: 50000, is_trending: false, is_recommended: true, promo_days: 45,
  },
  {
    id: 'static-growth_pro', name: 'Growth Pro', package_key: 'growth_pro', price: 10000, sort_order: 7,
    description: 'Professional growth package with 100,000 email blasts for maximum market penetration.',
    features: ['Everything in Enterprise Pro', '100,000 email blast to verified users', 'Cross-platform social promotion', '60-day trending placement', 'Influencer partnership outreach', 'Press release distribution', 'Advanced audience targeting', 'Dedicated campaign strategist'],
    email_count: 100000, is_trending: false, is_recommended: false, promo_days: 60,
  },
  {
    id: 'static-scale_elite', name: 'Scale Elite', package_key: 'scale_elite', price: 25000, sort_order: 8,
    description: 'Elite scaling solution with 200,000 email blasts and full-service campaign management.',
    features: ['Everything in Growth Pro', '200,000 email blast to verified users', 'Full-service campaign management', '90-day trending placement', 'Multi-exchange listing support', 'Community building package', 'AMA session coordination', 'KOL marketing campaign', 'Whitepaper promotion'],
    email_count: 200000, is_trending: false, is_recommended: false, promo_days: 90,
  },
  {
    id: 'static-enterprise_max', name: 'Enterprise Max', package_key: 'enterprise_max', price: 50000, sort_order: 9,
    description: 'Maximum enterprise solution with 500,000 email blasts and complete ecosystem support.',
    features: ['Everything in Scale Elite', '500,000 email blast to verified users', 'Complete ecosystem marketing', '120-day trending placement', 'Tier-1 exchange introduction', 'Institutional investor outreach', 'Global PR campaign', 'Token listing advisory', 'Dedicated marketing team'],
    email_count: 500000, is_trending: false, is_recommended: false, promo_days: 120,
  },
  {
    id: 'static-platinum', name: 'Platinum', package_key: 'platinum', price: 100000, sort_order: 10,
    description: 'The ultimate platinum package — 1,000,000 email blasts with full white-glove service.',
    features: ['Everything in Enterprise Max', '1,000,000 email blast to verified users', 'White-glove full-service management', '180-day trending placement', 'Tier-1 exchange listing support', 'Venture capital introduction', 'Global influencer network', 'Custom tokenomics advisory', 'Dedicated C-suite support', 'Lifetime premium listing'],
    email_count: 1000000, is_trending: false, is_recommended: false, promo_days: 180,
  },
];

// Allowed package keys — Pro Campaign is excluded
const ALLOWED_KEYS = new Set([
  'free', 'boost', 'starter_launch', 'growth_starter', 'enterprise',
  'enterprise_pro', 'growth_pro', 'scale_elite', 'enterprise_max', 'platinum',
]);

const PACKAGE_STYLES: Record<string, { border: string; accent: string; icon: React.ReactNode; glow: string }> = {
  free:           { border: 'border-[rgba(147,164,184,0.2)]',  accent: '#93A4B8', icon: <Package size={16} />,    glow: '' },
  boost:          { border: 'border-[rgba(22,194,255,0.25)]',  accent: '#16C2FF', icon: <Zap size={16} />,        glow: 'hover:shadow-[0_0_20px_rgba(22,194,255,0.1)]' },
  starter_launch: { border: 'border-[rgba(0,229,168,0.25)]',   accent: '#00E5A8', icon: <Rocket size={16} />,     glow: 'hover:shadow-[0_0_20px_rgba(0,229,168,0.1)]' },
  growth_starter: { border: 'border-[rgba(245,158,11,0.3)]',   accent: '#F59E0B', icon: <TrendingUp size={16} />, glow: 'hover:shadow-[0_0_20px_rgba(245,158,11,0.1)]' },
  enterprise:     { border: 'border-[rgba(139,92,246,0.3)]',   accent: '#8B5CF6', icon: <Star size={16} />,       glow: 'hover:shadow-[0_0_20px_rgba(139,92,246,0.1)]' },
  enterprise_pro: { border: 'border-[rgba(236,72,153,0.3)]',   accent: '#EC4899', icon: <Crown size={16} />,      glow: 'hover:shadow-[0_0_20px_rgba(236,72,153,0.1)]' },
  growth_pro:     { border: 'border-[rgba(249,115,22,0.3)]',   accent: '#F97316', icon: <TrendingUp size={16} />, glow: 'hover:shadow-[0_0_20px_rgba(249,115,22,0.1)]' },
  scale_elite:    { border: 'border-[rgba(16,185,129,0.3)]',   accent: '#10B981', icon: <Zap size={16} />,        glow: 'hover:shadow-[0_0_20px_rgba(16,185,129,0.1)]' },
  enterprise_max: { border: 'border-[rgba(59,130,246,0.3)]',   accent: '#3B82F6', icon: <Star size={16} />,       glow: 'hover:shadow-[0_0_20px_rgba(59,130,246,0.1)]' },
  platinum:       { border: 'border-[rgba(234,179,8,0.4)]',    accent: '#EAB308', icon: <Crown size={16} />,      glow: 'hover:shadow-[0_0_30px_rgba(234,179,8,0.15)]' },
};

const DEFAULT_STYLE = { border: 'border-[rgba(22,194,255,0.1)]', accent: '#16C2FF', icon: <Package size={16} />, glow: '' };

export default function AirdropPackagesPage() {
  const [packages, setPackages] = useState<AirdropPackage[]>(STATIC_PACKAGES);
  const [loading, setLoading] = useState(true);
  const [paymentModal, setPaymentModal] = useState<{ open: boolean; pkg: AirdropPackage | null }>({ open: false, pkg: null });
  const { walletUser, supabaseUser } = useWalletAuth();
  const isLoggedIn = !!(walletUser || supabaseUser);

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('airdrop_packages')
          .select('*')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (data && data.length > 0) {
          // Filter out Pro Campaign and any unknown keys, then sort ascending
          const filtered = data
            .filter(p => ALLOWED_KEYS.has(p.package_key))
            .map(p => ({ ...p, features: Array.isArray(p.features) ? p.features : [] }))
            .sort((a, b) => a.sort_order - b.sort_order);

          // If DB has all 10 allowed packages, use DB data; otherwise fall back to static
          if (filtered.length === 10) {
            setPackages(filtered);
          } else {
            // Merge: prefer DB data for known keys, fill gaps with static
            const dbMap = new Map(filtered.map(p => [p.package_key, p]));
            const merged = STATIC_PACKAGES.map(sp => dbMap.get(sp.package_key) || sp);
            setPackages(merged);
          }
        }
        // If DB returns nothing, STATIC_PACKAGES already set as default
      } finally {
        setLoading(false);
      }
    };
    loadPackages();
  }, []);

  return (
    <main className="min-h-screen bg-[#07111F]">
      <Header />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[rgba(22,194,255,0.08)] border border-[rgba(22,194,255,0.15)] rounded-full text-xs text-[#16C2FF] font-medium mb-4">
            <Package size={12} />
            Airdrop Packages
          </div>
          <h1 className="text-3xl sm:text-4xl font-sora font-bold text-[#E6F1FF] mb-4">
            Launch Your Airdrop on AIDARAM
          </h1>
          <p className="text-[#93A4B8] max-w-2xl mx-auto text-base">
            Choose the right package to reach thousands of crypto enthusiasts. From free listing to platinum full-service campaigns.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-[#93A4B8]">
            <span className="px-2 py-1 bg-[rgba(147,164,184,0.08)] border border-[rgba(147,164,184,0.15)] rounded-full">Free</span>
            <span className="text-[#4A5568]">→</span>
            <span className="px-2 py-1 bg-[rgba(234,179,8,0.08)] border border-[rgba(234,179,8,0.2)] rounded-full text-[#EAB308]">$100,000 Platinum</span>
          </div>
        </div>

        {/* Packages Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[1,2,3,4,5,6,7,8,9,10].map(i => (
              <div key={i} className="bg-[#0D1B2A] rounded-2xl p-6 animate-pulse h-80" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {packages.map(pkg => {
              const style = PACKAGE_STYLES[pkg.package_key] || DEFAULT_STYLE;
              const isPlatinum = pkg.package_key === 'platinum';
              return (
                <div
                  key={pkg.id}
                  className={`bg-[#0D1B2A] border ${style.border} rounded-2xl p-6 flex flex-col relative transition-all duration-200 ${style.glow} ${pkg.is_recommended ? 'ring-1 ring-[rgba(22,194,255,0.3)]' : ''} ${isPlatinum ? 'bg-gradient-to-b from-[#0D1B2A] to-[#111820]' : ''}`}
                >
                  {isPlatinum && (
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[rgba(234,179,8,0.04)] to-transparent pointer-events-none" />
                  )}

                  {pkg.is_recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#16C2FF] text-[#07111F] text-xs font-bold rounded-full whitespace-nowrap">
                      ⭐ RECOMMENDED
                    </div>
                  )}
                  {pkg.is_trending && !isPlatinum && (
                    <div className="absolute top-4 right-4">
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-[rgba(245,158,11,0.1)] text-[#F59E0B] text-xs rounded-full border border-[rgba(245,158,11,0.2)]">
                        <TrendingUp size={10} /> Trending
                      </span>
                    </div>
                  )}
                  {isPlatinum && (
                    <div className="absolute top-4 right-4">
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-[rgba(234,179,8,0.15)] text-[#EAB308] text-xs rounded-full border border-[rgba(234,179,8,0.3)]">
                        👑 PLATINUM
                      </span>
                    </div>
                  )}

                  {/* Package icon + name */}
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${style.accent}15`, color: style.accent, border: `1px solid ${style.accent}30` }}
                    >
                      {style.icon}
                    </div>
                    <h3 className="text-sm font-sora font-bold text-[#E6F1FF] leading-tight">{pkg.name}</h3>
                  </div>

                  <p className="text-xs text-[#93A4B8] mb-4 leading-relaxed">{pkg.description}</p>

                  {/* Price */}
                  <div className="mb-5">
                    <span className="text-2xl font-sora font-bold" style={{ color: style.accent }}>
                      {pkg.price === 0 ? 'Free' : `$${pkg.price.toLocaleString()}`}
                    </span>
                    {pkg.price > 0 && <span className="text-xs text-[#93A4B8] ml-1">one-time</span>}
                  </div>

                  {/* Email blast highlight */}
                  {pkg.email_count > 0 && (
                    <div
                      className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg"
                      style={{ background: `${style.accent}10`, border: `1px solid ${style.accent}25` }}
                    >
                      <Mail size={13} style={{ color: style.accent }} />
                      <span className="text-xs font-bold" style={{ color: style.accent }}>
                        {pkg.email_count >= 1000000
                          ? `${(pkg.email_count / 1000000).toFixed(0)}M`
                          : pkg.email_count >= 1000
                          ? `${(pkg.email_count / 1000).toFixed(0)}K`
                          : pkg.email_count.toLocaleString()
                        } Email Blast
                      </span>
                    </div>
                  )}

                  {/* Features */}
                  <ul className="space-y-1.5 flex-1 mb-5">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-[#93A4B8]">
                        <CheckCircle size={12} className="mt-0.5 flex-shrink-0" style={{ color: style.accent }} />
                        {feature}
                      </li>
                    ))}
                    {pkg.promo_days > 0 && (
                      <li className="flex items-center gap-2 text-xs font-medium" style={{ color: style.accent }}>
                        <Star size={12} />
                        {pkg.promo_days}-day trending placement
                      </li>
                    )}
                  </ul>

                  <button
                    onClick={() => {
                      if (pkg.price === 0) {
                        window.location.href = '/project-submission';
                        return;
                      }
                      setPaymentModal({ open: true, pkg });
                    }}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-xs font-semibold transition-all"
                    style={{
                      background: pkg.price === 0 ? 'rgba(147,164,184,0.1)' : `${style.accent}18`,
                      color: style.accent,
                      border: `1px solid ${style.accent}33`,
                    }}
                  >
                    {pkg.price === 0 ? 'Submit Free Listing' : 'Pay with Crypto'}
                    <ArrowRight size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center bg-[#0D1B2A] border border-[rgba(22,194,255,0.1)] rounded-2xl p-8">
          <h2 className="text-xl font-sora font-bold text-[#E6F1FF] mb-2">Need a Custom Package?</h2>
          <p className="text-[#93A4B8] mb-4">Contact us for enterprise deals, custom promotions, or bulk campaign pricing.</p>
          <a
            href="mailto:contact@aidaram.net"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#16C2FF] to-[#0EA5E9] text-[#07111F] font-semibold rounded-xl text-sm hover:opacity-90 transition-opacity"
          >
            <Mail size={14} />
            Contact Us
          </a>
        </div>
      </div>
      <Footer />
      <CryptoPaymentModal
        isOpen={paymentModal.open}
        onClose={() => setPaymentModal({ open: false, pkg: null })}
        packageName={paymentModal.pkg?.name || ''}
        packageId={paymentModal.pkg?.id || ''}
        amountUsd={paymentModal.pkg?.price || 0}
      />
    </main>
  );
}
